/*
  (c) 2025 Fluxonix Inc.
*/

import React, { useEffect, useState } from "react";
import { api } from "../api"; // your Axios wrapper
import {
  Box,
  Button,
  TextField,
  Typography,
  MenuItem,
  CircularProgress,
} from "@mui/material";

const PROVIDERS = ["gmail", "m365"];

const ConnectorConfigForm = () => {
  const [provider, setProvider] = useState("gmail");
  const [formData, setFormData] = useState({
    client_id: "",
    client_secret: "",
    redirect_uri: "",
    scopes: "",
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    const fetchConfig = async () => {
      setLoading(true);
      setStatusMessage("");
      try {
        const res = await api.get(`/api/admin/connectors/config/${provider}`);
        const { client_id, client_secret, redirect_uri, scopes } = res.data;
        setFormData({
          client_id,
          client_secret,
          redirect_uri,
          scopes: scopes.join(" "),
        });
      } catch (err) {
        // config might not exist
        setFormData({
          client_id: "",
          client_secret: "",
          redirect_uri: "",
          scopes: "",
        });
        if (err.response?.status !== 404) {
          setStatusMessage("❌ Failed to load config");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [provider]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatusMessage("");
    try {
      await api.post("/api/admin/connectors/config", {
        provider,
        client_id: formData.client_id,
        client_secret: formData.client_secret,
        redirect_uri: formData.redirect_uri,
        scopes: formData.scopes.trim().split(/\s+/),
      });
      setStatusMessage("✅ Config saved successfully");
    } catch (err) {
      setStatusMessage("❌ Failed to save config");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box p={2} maxWidth={600}>
      <Typography variant="h6">Connector Config</Typography>

      <TextField
        select
        fullWidth
        margin="normal"
        label="Provider"
        value={provider}
        onChange={(e) => setProvider(e.target.value)}
      >
        {PROVIDERS.map((p) => (
          <MenuItem key={p} value={p}>
            {p.toUpperCase()}
          </MenuItem>
        ))}
      </TextField>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <TextField
            fullWidth
            margin="normal"
            label="Client ID"
            name="client_id"
            value={formData.client_id}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Client Secret"
            name="client_secret"
            value={formData.client_secret}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Redirect URI"
            name="redirect_uri"
            value={formData.redirect_uri}
            onChange={handleChange}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Scopes (space-separated)"
            name="scopes"
            value={formData.scopes}
            onChange={handleChange}
          />

          <Box mt={2}>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Config"}
            </Button>
          </Box>
        </>
      )}

      {statusMessage && (
        <Typography mt={2} color="textSecondary">
          {statusMessage}
        </Typography>
      )}
    </Box>
  );
};

export default ConnectorConfigForm;